/**
 * Pure logic for the proposal review deck. Refs #24's last open box:
 * "pending proposals can be reviewed and approved in the UI".
 *
 * Split out from ProposalDeck.tsx/ProposalCard.tsx/useProposalQueue.ts the
 * same way touch.ts/voice.ts/proposals.ts are split out of the MCP edge
 * function: everything that can be wrong without a network call involved
 * lives here, tested under vitest; the React glue that fetches and renders is
 * untested by design.
 *
 * Two systems meet at this boundary and neither one gets bent to fit the
 * other:
 *
 * - `AgentWriteProposal` is read straight off `agent_write_proposals` through
 *   the ordinary Supabase data provider (PostgREST) — the table grants
 *   `select` to `authenticated` with a read-everything RLS policy, so listing
 *   what is pending needs no edge function at all. See
 *   supabase/schemas/06_grants.sql.
 * - A decision that WRITES (approve or reject) cannot go through
 *   `dataProvider.update`: flipping `status` from the browser would satisfy
 *   the column-level grant without ever replaying the tool call underneath
 *   it, which is exactly the failure mode #24 calls out. `toProposalActionRequest`
 *   is where that is enforced by construction — a `skip` decision has no
 *   branch that produces a request body, so there is no code path from
 *   "skip" to a network call. Approve/reject go to
 *   `POST .../mcp/app/proposals`, which replays the SAME `runProposalAction`
 *   crm_proposals uses server-side (see supabase/functions/mcp/index.ts for
 *   why that route exists and the transport decision behind it).
 *
 * `targetName` and the age bucketing intentionally duplicate small pieces of
 * supabase/functions/mcp/proposals.ts (snapshotName) and voice.ts
 * (humanizeAge) rather than importing them: this file runs in the Vite/vitest
 * world and that one runs on Deno with `npm:`-scheme imports, and
 * SwipeCard.tsx already keeps its own local humanizeAge/daysSince for the same
 * reason. Keep the two in sync by hand if the server-side phrasing changes.
 */

/** Lifecycle of a proposal. Mirrors the CHECK in 01_tables.sql. */
export type ProposalStatus =
  | "pending"
  | "approving"
  | "approved"
  | "rejected"
  | "expired"
  | "failed";

export type ProposalTargetType = "contact" | "company";

/**
 * A row of `agent_write_proposals`, as read back through the ordinary data
 * provider. Only the columns the deck actually uses — the table has more
 * (args_digest, proposed_by_uid, reviewed_at, execution_result, ledger_id,
 * ...) that a card never needs to show.
 */
export interface AgentWriteProposal {
  id: number;
  created_at: string;
  actor: string;
  tool: string;
  args: Record<string, unknown>;
  target_type: ProposalTargetType | null;
  target_id: number | null;
  target_snapshot: Record<string, unknown> | null;
  reason: string;
  suggested_next_step: string | null;
  source_ref: string | null;
  draft_question: string;
  status: ProposalStatus;
}

// --- Card content shaping ----------------------------------------------------

/**
 * The person or organization a proposal targets, from the snapshot taken at
 * propose time. Mirrors `snapshotName` in supabase/functions/mcp/proposals.ts.
 * A `create` proposal (a new contact, say) has no target yet — that is not a
 * bug, just a proposal whose card has nobody to name.
 */
export function targetName(proposal: AgentWriteProposal): string | null {
  const snapshot = proposal.target_snapshot;
  if (!snapshot) return null;
  if (proposal.target_type === "company") {
    return typeof snapshot.name === "string" && snapshot.name
      ? snapshot.name
      : null;
  }
  if (proposal.target_type === "contact") {
    const name = [snapshot.first_name, snapshot.last_name]
      .filter((part): part is string => typeof part === "string" && part !== "")
      .join(" ");
    return name || null;
  }
  return null;
}

/** "crm_set_relationship_play" -> "Set relationship play". */
export function humanizeToolName(tool: string): string {
  const words = tool.replace(/^crm_/, "").split("_").filter(Boolean);
  if (words.length === 0) return tool;
  return [
    words[0].charAt(0).toUpperCase() + words[0].slice(1),
    ...words.slice(1),
  ].join(" ");
}

/** One readable value, never raw JSON for a scalar. */
function describeArgValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) {
    return value.length === 0 ? "none" : value.map(String).join(", ");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  // A date-looking string is shown as just the day; anything longer than a
  // sentence-ish length is not, since long free text has its own field
  // (reason, draft_question) and does not belong crammed into a "key: value"
  // line.
  const text = String(value);
  const isoDate = /^\d{4}-\d{2}-\d{2}(?:[T ]|$)/.exec(text);
  return isoDate ? text.slice(0, 10) : text;
}

/** "reason" -> "Reason", "next_touch_date" -> "Next touch date". */
function humanizeArgKey(key: string): string {
  const words = key.split("_").filter(Boolean);
  if (words.length === 0) return key;
  return (
    words[0].charAt(0).toUpperCase() +
    words[0].slice(1) +
    " " +
    words.slice(1).join(" ")
  ).trim();
}

/** Args worth its own line vs. args better left to the reason/question prose. */
const SKIP_ARG_KEYS = new Set([
  "confirmed",
  "target_type",
  "target_id",
  "contact_id",
  "company_id",
]);

/**
 * `tool` + `args`, rendered as the human needs to judge them — not the raw
 * JSON blob. One "Key: value" line per argument that is not plumbing (the
 * target id, which the card already names by its target, or the confirmation
 * flag, which is always true by the time a proposal exists).
 */
export function describeProposedCall(
  proposal: Pick<AgentWriteProposal, "tool" | "args">,
): { toolLabel: string; lines: { label: string; value: string }[] } {
  const lines = Object.entries(proposal.args)
    .filter(([key, value]) => !SKIP_ARG_KEYS.has(key) && value !== undefined)
    .map(([key, value]) => ({
      label: humanizeArgKey(key),
      value: describeArgValue(value),
    }));
  return { toolLabel: humanizeToolName(proposal.tool), lines };
}

/** Days between two instants, floor-rounded — negative means `then` is future. */
function daysBetween(then: string | Date, now: Date): number {
  const thenMs = then instanceof Date ? then.getTime() : Date.parse(then);
  return Math.floor((now.getTime() - thenMs) / 86_400_000);
}

/**
 * How long a proposal has been waiting. Matches SwipeCard's own
 * humanizeAge/daysSince bucketing so the two decks read consistently.
 */
export function proposalAge(createdAt: string | Date, now: Date): string {
  const days = daysBetween(createdAt, now);
  if (Number.isNaN(days)) return "unknown age";
  if (days <= 0) return "today";
  if (days === 1) return "1 day";
  if (days < 30) return `${days} days`;
  if (days < 365) {
    const months = Math.round(days / 30);
    return `${months} ${months === 1 ? "month" : "months"}`;
  }
  const years = Math.round(days / 365);
  return `${years} ${years === 1 ? "year" : "years"}`;
}

// --- Queue ordering -----------------------------------------------------------

/**
 * The buffer a deck shows, from a fetched page plus local bookkeeping.
 *
 * Mirrors `buffer` in useSwipeQueue.ts: a card taken off the top locally
 * (`consumed`) must not reappear just because the underlying fetch has not
 * caught up yet, and — unlike the contact deck, which never puts a card back —
 * this queue has no undo, so there is no `restored` list to merge ahead of it.
 * What is shared is the dedupe-by-id: a proposal a refetch serves again while
 * still `consumed` locally (a slow network, a stale page) must not show twice.
 */
export function buildProposalQueue(
  fetched: readonly AgentWriteProposal[],
  consumed: ReadonlySet<number>,
): AgentWriteProposal[] {
  const seen = new Set<number>();
  const out: AgentWriteProposal[] = [];
  for (const proposal of fetched) {
    if (consumed.has(proposal.id) || seen.has(proposal.id)) continue;
    seen.add(proposal.id);
    out.push(proposal);
  }
  return out;
}

// --- Decisions ----------------------------------------------------------------

export type ProposalDecision =
  | { type: "approve"; id: number; reviewedBy: string }
  | { type: "reject"; id: number; reviewedBy: string; rejectionReason: string }
  | { type: "skip"; id: number };

export type BuildDecisionResult =
  | { ok: true; decision: ProposalDecision }
  | { ok: false; error: string };

/** Always succeeds: there is nothing to validate about approving. */
export function buildApproveDecision(
  id: number,
  reviewedBy: string,
): BuildDecisionResult {
  const name = reviewedBy.trim();
  if (!name) {
    return { ok: false, error: "Sign in with a name before approving." };
  }
  return { ok: true, decision: { type: "approve", id, reviewedBy: name } };
}

/**
 * A rejection needs a reason: the queue exists so a bad call is traceable to
 * its input (AGENTS.md's own rule for the triage passes that feed proposals),
 * and "rejected, no reason given" teaches the proposer nothing. Whitespace-only
 * input is treated as no input.
 */
export function buildRejectDecision(
  id: number,
  reviewedBy: string,
  rejectionReason: string,
): BuildDecisionResult {
  const name = reviewedBy.trim();
  if (!name) {
    return { ok: false, error: "Sign in with a name before rejecting." };
  }
  const reason = rejectionReason.trim();
  if (!reason) {
    return {
      ok: false,
      error:
        "Say why, in a sentence the proposer can learn from — a rejection with no reason is a rejection nobody can act on.",
    };
  }
  return {
    ok: true,
    decision: { type: "reject", id, reviewedBy: name, rejectionReason: reason },
  };
}

/** Always succeeds: skip has nothing to validate either. */
export function buildSkipDecision(id: number): ProposalDecision {
  return { type: "skip", id };
}

/**
 * The body `POST .../mcp/app/proposals` expects, or `null` for a decision
 * that must write nothing.
 *
 * `skip` returns `null` unconditionally. That is not a convention this
 * function is trusted to follow — it is the only thing this function can
 * return for that case, so a caller cannot reach a fetch() from a skip by
 * forgetting a guard somewhere else. See useProposalQueue.ts, whose `skip`
 * never calls this at all, for the belt-and-braces version of the same fact.
 */
export function toProposalActionRequest(
  decision: ProposalDecision,
): Record<string, unknown> | null {
  switch (decision.type) {
    case "skip":
      return null;
    case "approve":
      return {
        action: "approve",
        id: decision.id,
        reviewed_by: decision.reviewedBy,
      };
    case "reject":
      return {
        action: "reject",
        id: decision.id,
        reviewed_by: decision.reviewedBy,
        rejection_reason: decision.rejectionReason,
      };
  }
}

// --- Outcome interpretation -----------------------------------------------------

/**
 * How a card reports what actually happened. `approved`/`approved_with_drift`
 * are both real successes — the split exists so the UI can say "heads up"
 * without treating drift as failure, matching the issue's own "warn, don't
 * block" decision on stale-target drift.
 */
export type ProposalOutcome =
  | { kind: "approved"; speech: string }
  | { kind: "approved_with_drift"; speech: string; drift: readonly string[] }
  | { kind: "rejected"; speech: string }
  | { kind: "failed"; speech: string }
  | { kind: "not_claimed"; speech: string; status: string }
  | { kind: "error"; speech: string };

/** A `POST .../mcp/app/proposals` response body is not proof it is shaped right. */
function asRecord(body: unknown): Record<string, unknown> {
  return body && typeof body === "object"
    ? (body as Record<string, unknown>)
    : {};
}

const stringField = (
  body: Record<string, unknown>,
  key: string,
): string | undefined =>
  typeof body[key] === "string" ? (body[key] as string) : undefined;

/**
 * Read the outcome of an approve call. Three shapes, matching what
 * `runProposalAction`'s approve branch actually returns (see
 * supabase/functions/mcp/index.ts): `status: "approved"` with a possibly-empty
 * `target_drift` array, `status: "failed"` (the replay ran and the tool
 * refused it, or threw), or `status: "not_claimed"` (someone/something else
 * claimed it first — routine, not a failure). Anything else — a network
 * error, a body that is not JSON, an `error` field from the 400/401 paths —
 * is `"error"`, so the card never silently reads a failed request as success.
 */
export function interpretApproveResponse(
  body: unknown,
  httpOk: boolean,
): ProposalOutcome {
  const record = asRecord(body);
  const errorText = stringField(record, "error");
  if (!httpOk || errorText) {
    return {
      kind: "error",
      speech: errorText ?? "The approval request failed.",
    };
  }
  const status = stringField(record, "status");
  const speech =
    stringField(record, "speech") ?? stringField(record, "message");
  if (status === "approved") {
    const drift = Array.isArray(record.target_drift)
      ? record.target_drift.filter((d): d is string => typeof d === "string")
      : [];
    return drift.length > 0
      ? { kind: "approved_with_drift", speech: speech ?? "Approved.", drift }
      : { kind: "approved", speech: speech ?? "Approved." };
  }
  if (status === "failed") {
    return {
      kind: "failed",
      speech: speech ?? "The proposal did not go through.",
    };
  }
  if (status === "not_claimed") {
    const proposalStatus = stringField(record, "proposal_status") ?? "unknown";
    return {
      kind: "not_claimed",
      speech: speech ?? `That proposal is already ${proposalStatus}.`,
      status: proposalStatus,
    };
  }
  return {
    kind: "error",
    speech: speech ?? "Unrecognised response from the server.",
  };
}

/** Read the outcome of a reject call. Simpler: reject has no drift to report. */
export function interpretRejectResponse(
  body: unknown,
  httpOk: boolean,
): ProposalOutcome {
  const record = asRecord(body);
  const errorText = stringField(record, "error");
  if (!httpOk || errorText) {
    return {
      kind: "error",
      speech: errorText ?? "The rejection request failed.",
    };
  }
  const status = stringField(record, "status");
  const speech =
    stringField(record, "speech") ?? stringField(record, "message");
  if (status === "rejected") {
    return { kind: "rejected", speech: speech ?? "Rejected." };
  }
  if (status === "not_changed") {
    const proposalStatus = stringField(record, "proposal_status") ?? "unknown";
    return {
      kind: "not_claimed",
      speech: speech ?? `That proposal is already ${proposalStatus}.`,
      status: proposalStatus,
    };
  }
  return {
    kind: "error",
    speech: speech ?? "Unrecognised response from the server.",
  };
}
